import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'immutable';

import { Block, CoreLink, Paragraph } from '@uncinc/react-drupal-core';
import {
  Button,
  Container,
  Image,
  NavigationScroll,
  useWindowDimensions,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  BLOCK_NEWSLETTER_SUBSCRIBE_BLOCK_ID,
  PARAGRAPH_VIEW_MODE_CAMPAIGN_COLOR_THEME,
  SCREEN_MOBILE,
  SCREEN_TABLET,
  SCREEN_NORMAL,
} from '../../../config/constants';

import { ReactComponent as RGBLogo } from '../../../assets/images/dgb-logo.svg';

import './style.scss';

const CampaignCTA = ({ CTAImage, CTAtitle, CTAsubTitle, CTAlink, CTAlinkSecondary }) => (
  <div className="NodeCampaignPageColorTheme__cta">
    <div className="NodeCampaignPageColorTheme__cta__image">
      <Image src={CTAImage} />
    </div>
    <div className="NodeCampaignPageColorTheme__cta__body">
      <h3>{CTAtitle}</h3>
      <h1>{CTAsubTitle}</h1>
      <Button
        className="NodeCampaignPageColorTheme__cta__button"
        href={CTAlink.alias ?? CTAlink.uri}
      >
        {CTAlink.title}
      </Button>
      {CTAlinkSecondary && (
        <CoreLink
          className="NodeCampaignPageColorTheme__cta__link"
          href={CTAlinkSecondary.alias ?? CTAlinkSecondary.uri}
        >
          {CTAlinkSecondary.title}
          <i className="icon icon-arrow-right icon-variant-2" />
        </CoreLink>
      )}
    </div>
  </div>
);

const NodeCampaignPageColorTheme = ({ entity }) => {
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  const campaignPage = entity.toJS();
  const title = campaignPage.title[0].value;
  const body = campaignPage.body[0].processed;
  const heroImageStyles = campaignPage.field_image[0].styles;
  const heroSVG = campaignPage.field_svg[0]?.source;

  const CTAtitle = campaignPage.field_title[0].value;
  const CTAsubTitle = campaignPage.field_capacity[0].value;
  const CTAlink = campaignPage.field_link[0];
  const CTAlinkSecondary = campaignPage.field_link_to_page[0];
  const CTAimageStyles = campaignPage.field_image_secondary[0].styles;

  const heroImage = {
    [SCREEN_MOBILE]: heroImageStyles.crop_4_3,
    [SCREEN_TABLET]: heroImageStyles.crop_16_9,
  };

  const CTAImage = {
    [SCREEN_MOBILE]: CTAimageStyles.crop_4_3,
    [SCREEN_TABLET]: CTAimageStyles.crop_1_1,
    [SCREEN_NORMAL]: CTAimageStyles.crop_4_3,
  };

  // In case someone forgets to put the # before the HEXcode
  let themeColor = campaignPage.field_label[0].value;
  if (themeColor[0] !== '#') {
    themeColor = `#${themeColor}`;
  }

  const { width } = useWindowDimensions();
  const isNormal = useMemo(() => { return width >= SCREEN_NORMAL; }, [width]);

  // For the sticky CTA
  const wrapper = useRef(null);
  const ctaColumn = useRef(null);

  const setStickyHeight = () => {
    if (wrapper.current && ctaColumn.current && isNormal) {
      ctaColumn.current.style.height = `${wrapper.current.getBoundingClientRect().height}px`;
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', setStickyHeight);
    window.addEventListener('resize', setStickyHeight);

    return () => {
      window.removeEventListener('scroll', setStickyHeight);
      window.removeEventListener('resize', setStickyHeight);
    };
  });

  const themeStyling = `
    .NodeCampaignPageColorTheme__header__link {
      svg path {
        fill: white !important;
      }
    }

    .NavigationScroll.s-scrolled-past-nav-height,
    .NodeCampaignPageColorTheme__background,
    .NodeCampaignPageColorTheme__hero__title,
    .NodeCampaignPageColorTheme__cta,
    .Footer__bottom {
      background-color: ${themeColor};
    }

    .Paragraphs {
      h2,
      .ParagraphQuote__text p {
        color: ${themeColor};
      }
    }

    .Footer {
      .Footer__menu__overlay {
        svg path {
          fill: ${themeColor};
        }
      }
    }
  `;

  return (
    <div className="Node NodeCampaignPageColorTheme">
      <style>
        {themeStyling}
      </style>

      <NavigationScroll className="NodeCampaignPageColorTheme__header">
        <CoreLink
          className="NodeCampaignPageColorTheme__header__link"
          href={`/${currentLanguage}/`}
        >
          <RGBLogo className="NodeCampaignPageColorTheme__header__logo" alt="De Regenboog Groep logo" />
        </CoreLink>
      </NavigationScroll>

      <div id="NodeCampaignPageColorTheme__background" className="NodeCampaignPageColorTheme__background">
        {heroSVG && (
          <img src={heroSVG} alt="campaign-background-pattern" />
        )}
      </div>

      <Container className="NodeCampaignPageColorTheme__hero">
        <div className="row">
          <div className="col-12">
            <div className="NodeCampaignPageColorTheme__hero__image">
              <Image src={heroImage} />
            </div>
          </div>
        </div>
      </Container>

      <div ref={wrapper} className="NodeCampaignPageColorTheme__wrapper">
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-7 offset-lg-0">
              <h2 className="NodeCampaignPageColorTheme__hero__title">
                {title}
              </h2>
              <WysiwygField html={body} className="NodeCampaignPageColorTheme__hero__body" />
            </div>
            <div ref={ctaColumn} className="cta-column col-12 col-md-10 offset-md-1 col-lg-4 offset-lg-1">
              <CampaignCTA
                CTAImage={CTAImage}
                CTAtitle={CTAtitle}
                CTAsubTitle={CTAsubTitle}
                CTAlink={CTAlink}
                CTAlinkSecondary={CTAlinkSecondary}
              />
            </div>
          </div>
        </Container>
        {entity.get('field_paragraphs') && (
          <div className="Paragraphs node--page--paragraphs-wrapper">
            {entity.get('field_paragraphs', List()).map((paragraph) => {
              const id = paragraph.get('target_id');
              return (
                <Paragraph
                  key={`paragraph:${id}`}
                  id={id}
                  entity={paragraph.get('entity')}
                  viewMode={PARAGRAPH_VIEW_MODE_CAMPAIGN_COLOR_THEME}
                />
              );
            })}
          </div>
        )}
        {!isNormal && (
          <Container>
            <div className="row">
              <div className="col-12 col-md-10 offset-md-1">
                <CampaignCTA
                  CTAImage={CTAImage}
                  CTAtitle={CTAtitle}
                  CTAsubTitle={CTAsubTitle}
                  CTAlink={CTAlink}
                  CTAlinkSecondary={CTAlinkSecondary}
                />
              </div>
            </div>
          </Container>
        )}
      </div>

      <Block id={BLOCK_NEWSLETTER_SUBSCRIBE_BLOCK_ID} />
    </div>
  );
};

export default NodeCampaignPageColorTheme;
